import { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Space, App as AntdApp } from 'antd';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import type { FormProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSendOTP, useVerifyOTP } from '../../hooks/useAuth';
import { decryptData } from '../../utils/decrypt';

const { Link, Text } = Typography;

// Interface for email form values
interface EmailFormValues {
  email: string;
}

// Interface for OTP form values
interface OTPFormValues {
  otp: string;
}

const ForgotPassword = () => {
  const { message } = AntdApp.useApp();
  const [emailForm] = Form.useForm<EmailFormValues>();
  const [otpForm] = Form.useForm<OTPFormValues>();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(0);
  const [captchaToken, setCaptchaToken] = useState("");

  const { mutateAsync: sendOtp, isPending: sendOtpLoading } = useSendOTP();
  const { mutateAsync: verifyOtp, isPending: verifyOtpLoading } = useVerifyOTP();

  const navigate = useNavigate();
  // Countdown timer for resend OTP
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
  const getCaptcha = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/meeting-request/captcha-key`
      );

      const data = await res.json();

      const decrypted = decryptData(data, data.iv);

      setCaptchaToken(decrypted.token);
    } catch (err) {
      console.error("captcha error", err);
    }
  };

  getCaptcha();
}, []);

  const onEmailFinish: FormProps<EmailFormValues>['onFinish'] = async (values) => {
    if (!values) return;

    try {
      await sendOtp({
  email: values.email,
  captchaToken: captchaToken,
  website: "",
});

      setEmail(values.email);
      setStep('otp');
      setCountdown(60); // 60 seconds countdown
      message.success('OTP sent to your email address!');

      // Clear email form
      emailForm.resetFields();
    } catch (error: unknown) {
        console.error('Send OTP error:', error);

  const err = error as any;

  if (err?.statusCode === 429) {
    console.log("Errror is that ",err)
    message.error("Too many attempts. Please try again after some time.");
  } else {
    message.error(
      err?.response?.data?.message ||
      err?.message ||
      'Failed to send OTP. Please try again.'
    );
  }
    }
  };

  const onOTPFinish: FormProps<OTPFormValues>['onFinish'] = async (values) => {
    if (!values) return;

    try {
 const response = await verifyOtp({ email, otp: values.otp });

const resetToken = response.resetToken;

navigate('/reset-password', { state: { resetToken } });

      // Clear OTP form
      otpForm.resetFields();
    } catch (error: unknown) {
      console.error('Verify OTP error:', error);
      const errorMessage =
        (error as { message?: string; error?: string })?.message ||
        (error as { message?: string; error?: string })?.error ||
        'Invalid OTP. Please try again.';
      message.error(errorMessage);
    }
  };

  const onEmailFinishFailed: FormProps<EmailFormValues>['onFinishFailed'] = (errorInfo) => {
    console.log('Email form validation failed:', errorInfo);
    message.error('Please check your email and try again.');
  };

  const onOTPFinishFailed: FormProps<OTPFormValues>['onFinishFailed'] = (errorInfo) => {
    console.log('OTP form validation failed:', errorInfo);
    message.error('Please enter a valid 6-digit OTP.');
  };

  const handleResendOTP = async (): Promise<void> => {
    try {
      await sendOtp({
  email: email,
  captchaToken: captchaToken,
  website: "",
});
      setCountdown(60); // Reset countdown
      message.success('OTP resent successfully!');
    } catch (error: unknown) {
      console.error('Resend OTP error:', error);
      const errorMessage =
        (error as { message?: string; error?: string })?.message ||
        (error as { message?: string; error?: string })?.error ||
        'Failed to resend OTP. Please try again.';
      message.error(errorMessage);
    }
  };

  const handleBackToEmail = (): void => {
    setStep('email');
    setCountdown(0);
    otpForm.resetFields();
  };

  const handleBackToLogin = (): void => {
    console.log('Navigate back to login');
    navigate('/login');
  };

  // Email validation rules
  const emailRules = [
    {
      required: true,
      message: 'Please input your email!',
    },
    {
      type: 'email' as const,
      message: 'Please enter a valid email address!',
    },
  ];

  // OTP validation rules
  const otpRules = [
    {
      required: true,
      message: 'Please input the OTP!',
    },
    {
      len: 6,
      message: 'OTP must be exactly 6 digits!',
    },
    {
      pattern: /^\d{6}$/,
      message: 'OTP must contain only numbers!',
    },
  ];

  if (step === 'email') {
    return (
      <div className="!mt-8">
        <div className="!mb-6">
          <Text className="text-gray-600 text-sm">
            Enter your email address and we'll send you a verification code to reset your password.
          </Text>
        </div>

        <Form<EmailFormValues>
          form={emailForm}
          name="forgotPasswordEmailForm"
          onFinish={onEmailFinish}
          onFinishFailed={onEmailFinishFailed}
          autoComplete="off"
          layout="vertical"
          size="large"
          validateTrigger="onBlur"
        >
          <Form.Item<EmailFormValues>
            label={<span className="text-sm font-medium text-gray-700">Email Address</span>}
            name="email"
            rules={emailRules}
            hasFeedback
          >
            <Input
              prefix={<Mail size={16} className="text-gray-400" />}
              placeholder="Enter your email address"
              className="rounded-lg h-12 text-base "
              type="email"
            />
          </Form.Item>
           <input
  type="text"
  name="website"
  style={{ display: "none" }}
  autoComplete="off"
/>
          <Form.Item className="mb-6">
            <Button
              type="primary"
              htmlType="submit"
              loading={sendOtpLoading}
              block
              className="h-12 rounded-lg text-base font-semibold shadow-md hover:shadow-lg"
              disabled={sendOtpLoading}
            >
              {sendOtpLoading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          </Form.Item>

          <div className="text-center">
            <Link
              onClick={handleBackToLogin}
              className="text-sm font-medium !text-[#fc4c02]  hover:!text-[#fc4c02] cursor-pointer transition-colors duration-200 inline-flex items-center gap-1"
            >
              <ArrowLeft size={14} />
              Back to Login
            </Link>
          </div>
        </Form>
      </div>
    );
  }

  return (
    <div className="!mt-8">
      <div className="!mb-6">
        <Text className="text-gray-600 text-sm">
          We've sent a 6-digit verification code to{' '}
          <span className="font-medium text-gray-800">{email}</span>
        </Text>
      </div>

      <Form<OTPFormValues>
        form={otpForm}
        name="forgotPasswordOTPForm"
        onFinish={onOTPFinish}
        onFinishFailed={onOTPFinishFailed}
        autoComplete="off"
        layout="vertical"
        size="large"
        validateTrigger="onBlur"
      >
        <Form.Item<OTPFormValues>
          label={<span className="text-sm font-medium text-gray-700">Verification Code</span>}
          name="otp"
          rules={otpRules}
          hasFeedback
        >
          <Input
            placeholder="Enter 6-digit code"
            className="rounded-lg h-12 text-base text-center tracking-widest font-mono "
            maxLength={6}
            style={{ letterSpacing: '0.5em' }}
          />
        </Form.Item>

        <Form.Item className="mb-4">
          <Button
            type="primary"
            htmlType="submit"
            loading={verifyOtpLoading}
            block
            className="h-12 rounded-lg text-base font-semibold bg-blue-500 hover:bg-blue-600 border-blue-500 hover:border-blue-600 transition-all duration-200 shadow-md hover:shadow-lg"
            disabled={verifyOtpLoading}
          >
            {verifyOtpLoading ? 'Verifying...' : 'Verify OTP'}
          </Button>
        </Form.Item>

        <div className="text-center mb-4">
          {countdown > 0 ? (
            <Text className="text-sm text-gray-500">Resend OTP in {countdown} seconds</Text>
          ) : (
            <Space>
              <Text className="text-sm text-gray-500">Didn't receive the code?</Text>
              <Link
                onClick={handleResendOTP}
                className="text-sm font-medium !text-[#fc4c02]  hover:!text-[#fc4c02] cursor-pointer transition-colors duration-200 inline-flex items-center gap-1"
                disabled={sendOtpLoading}
              >
                <RefreshCw size={12} className={sendOtpLoading ? 'animate-spin' : ''} />
                {sendOtpLoading ? 'Resending...' : 'Resend OTP'}
              </Link>
            </Space>
          )}
        </div>

        <div className="text-center">
          <Link
            onClick={handleBackToEmail}
            className="text-sm font-medium !text-[#fc4c02]  hover:!text-[#fc4c02] cursor-pointer transition-colors duration-200 inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Change Email
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default ForgotPassword;
